import { STATUS_TAG_LABEL } from './status-codes';
import type { WrkthruStatusCode } from './status-codes';
import type { WrkthruIssue } from './types';
import type { PageContext } from '../api/page-context-types';

// ---------------------------------------------------------------------------
// Wrkthru It seed — built purely from the WrkthruIssue (no I/O).
// ---------------------------------------------------------------------------

/** Same origin resumes a Conversation; a different Issue on the same entity starts a new one. */
export type WrkthruItOrigin = {
  entityType: 'lineItem' | 'campaign';
  entityId: string;
  issueCode: WrkthruStatusCode;
};

export type WrkthruItSeed = {
  message: string;
  /** Stamped with the Issue's entity, not derived from the /wrkthru route (which has no entity in focus). */
  pageContext: PageContext;
  title: string;
  origin: WrkthruItOrigin;
};

export function buildWrkthruItSeed(issue: WrkthruIssue): WrkthruItSeed {
  const statusLabel = STATUS_TAG_LABEL[issue.statusCode];
  const isCampaign = issue.scope === 'campaign';
  const noun = isCampaign ? 'campaign' : 'line item';

  const message = buildSeedBody({
    header: `Wrkthru flagged "${statusLabel}" on my ${noun} ${issue.entityName}.`,
    evidenceLabel: 'Evidence',
    evidence: issue.description,
    whyNow: issue.whyNow,
    closing: `Explain what's going on with this ${noun} and lay out my options for addressing it.`
  });

  return {
    message,
    pageContext: {
      route: issue.entityHref,
      entity: isCampaign ? 'campaign' : 'lineItem',
      id: issue.id,
      label: issue.entityName,
      view: isCampaign ? 'overview' : 'execution'
    },
    title: `${statusLabel} — ${issue.entityName}`,
    origin: {
      entityType: isCampaign ? 'campaign' : 'lineItem',
      entityId: issue.id,
      issueCode: issue.statusCode
    }
  };
}

type SeedBody = {
  header: string;
  evidenceLabel: string;
  evidence: string;
  whyNow?: string;
  closing: string;
};

function buildSeedBody({ header, evidenceLabel, evidence, whyNow, closing }: SeedBody): string {
  const lines = [header, '', `${evidenceLabel}: ${evidence}`];
  if (whyNow) lines.push(`Why now: ${whyNow}`);
  lines.push('', closing);
  return lines.join('\n');
}
